import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/Button/Button";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import SearchInput from "@/components/SearchInput/SearchInput";
import { RecurringEmail } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmails/types";
import { RecurringEmailRecipient } from "@/modules/emailManagement/features/RecurringEmailManagement/api/fetchRecurringEmailRecipients/types";
import UnsubscribeRecipientModal from "@/modules/emailManagement/features/RecurringEmailManagement/components/UnsubscribeRecipientModal/UnsubscribeRecipientModal";
import { useRecurringEmailCard } from "@/modules/emailManagement/features/RecurringEmailManagement/hooks/useRecurringEmailCard";

interface RecurringEmailCardProps {
  recurringEmail: RecurringEmail;
  onUnsubscribed: () => void;
}

const subscribedOnFormat = new Intl.DateTimeFormat("en-US", {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
});

function formatSubscribedOn(subscribedAt: string | null): string {
  if (!subscribedAt) return "";

  return subscribedOnFormat.format(new Date(subscribedAt));
}

function describeSubscriberCount(subscriberCount: number): string {
  if (subscriberCount === 0) return "No subscribers";
  if (subscriberCount === 1) return "1 subscriber";

  return `${subscriberCount} subscribers`;
}

const RecurringEmailCard: React.FC<RecurringEmailCardProps> = ({
  recurringEmail,
  onUnsubscribed,
}) => {
  const {
    isExpanded,
    toggle,
    isLoading,
    recipients,
    filteredRecipients,
    pagedRecipients,
    pageIndex,
    pageSize,
    changePage,
    searchTerm,
    setSearchTerm,
    recipientToUnsubscribe,
    requestUnsubscribe,
    cancelUnsubscribe,
    confirmUnsubscribe,
    isUnsubscribing,
  } = useRecurringEmailCard(
    recurringEmail.recurringEmail,
    recurringEmail.label,
    onUnsubscribed,
  );

  const columns: Column<RecurringEmailRecipient>[] = [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{row.original.name}</span>
          {!row.original.isEligible && (
            <Badge
              title="Not currently sent — employee terminated or company inactive."
              variant="grey"
            >
              Inactive
            </Badge>
          )}
        </div>
      ),
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "companyName", header: "Company" },
    {
      id: "subscribedAt",
      header: "Subscribed on",
      cell: ({ row }) => formatSubscribedOn(row.original.subscribedAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          className="rounded-md"
          onClick={() => requestUnsubscribe(row.original)}
          size="xs"
          variant="outline"
        >
          Unsubscribe
        </Button>
      ),
    },
  ];

  const bodyId = `recurring-email-${recurringEmail.recurringEmail}`;

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
      <button
        aria-controls={isExpanded ? bodyId : undefined}
        aria-expanded={isExpanded}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
        onClick={toggle}
        type="button"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 mt-0.5 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 mt-0.5 text-gray-500 dark:text-gray-400" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              {recurringEmail.label}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {describeSubscriberCount(recurringEmail.subscriberCount)}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {recurringEmail.description}
          </p>
        </div>
      </button>

      {isExpanded && (
        <div
          className="border-t border-gray-200 dark:border-slate-700 p-4 space-y-4"
          id={bodyId}
        >
          <SearchInput
            className="max-w-sm"
            onChange={setSearchTerm}
            placeholder="Search by name, email or company"
            value={searchTerm}
          />
          <DataTable
            columns={columns}
            data={pagedRecipients}
            loading={isLoading}
            noDataMessage={
              recipients.length === 0
                ? "No subscribers yet. Subscribe employees from their Edit User page."
                : "No subscribers match your search."
            }
            onPageChange={changePage}
            pageIndex={pageIndex}
            pageSize={pageSize}
            rowKeyExtractor={(item) => item.uuid}
            totalCount={filteredRecipients.length}
          />
        </div>
      )}

      <UnsubscribeRecipientModal
        isSubmitting={isUnsubscribing}
        label={recurringEmail.label}
        onClose={cancelUnsubscribe}
        onConfirm={confirmUnsubscribe}
        recipient={recipientToUnsubscribe}
      />
    </div>
  );
};

export default RecurringEmailCard;
